import React, { useState } from 'react';
import { Form, Button, Row, Col, Card, Spinner, Alert, Modal, ListGroup, Tab, Tabs } from 'react-bootstrap';
import { FaExchangeAlt, FaUser, FaUsers, FaCreditCard, FaUndo, FaCalculator } from 'react-icons/fa';
import api from '../services/api';

const DMTBank4Modal = ({ show, onHide, onSuccess }) => {
  const [activeTab, setActiveTab] = useState('remitter');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Remitter Data
  const [remitterData, setRemitterData] = useState({
    mobile: '',
    name: '',
    email: '',
    address: '',
    pincode: '',
    city: '',
    state: '',
    pan_number: '',
    aadhar_number: '',
    // Required fields from Remitter KYC
    ekyc_id: '',
    otp: '',
    stateresp: '',
    piddata: '',
    accessmode: 'SITE | APP'
  });

  // KYC Data
  const [kycData, setKycData] = useState({
    mobile: '',
    kyc_type: 'aadhar',
    kyc_data: '',
    customerreferenceno: ''  // From Query Remitter response_code 2
  });

  // Beneficiary Data
  const [beneficiaryData, setBeneficiaryData] = useState({
    mobile: '',
    bene_name: '',
    bene_account: '',
    bene_ifsc: '',
    bank_id: '',
    bene_bank: '',
    bene_mobile: ''
  });

  // Transaction Data
  const [transactionData, setTransactionData] = useState({
    mobile: '',
    amount: '',
    bene_id: '',
    otp: '',
    reference_id: '',
    stateresp: ''  // From Send OTP response
  });

  // Refund Data
  const [refundData, setRefundData] = useState({
    reference_id: '',
    otp: ''
  });

  // Status Check Data
  const [statusData, setStatusData] = useState({
    reference_id: ''
  });

  // Beneficiaries List
  const [beneficiaries, setBeneficiaries] = useState([]);

  // Quick amounts for transactions
  const quickAmounts = [100, 200, 500, 1000, 2000, 5000, 10000, 25000];

  // Query Remitter
  const handleQueryRemitter = async () => {
    if (!remitterData.mobile) {
      setError('Please enter mobile number');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await api.post('/api/dmt-bank4/remitter/query', {
        mobile: remitterData.mobile
      });

      if (response.data.success) {
        if (response.data.data?.response_code === 1) {
          setSuccess('Remitter found! You can proceed to add beneficiaries and make transactions.');
          setActiveTab('beneficiary'); // Move to beneficiary tab
          await fetchBeneficiaries();
        } else if (response.data.data?.response_code === 2) {
          setSuccess('Please complete KYC and registration process.');
          setActiveTab('kyc'); // Move to KYC tab
          
          // Extract customerreferenceno from Query Remitter response (response_code 2)
          const customerRefNo = response.data.data?.data?.customerreferenceno 
            || response.data.data?.customerreferenceno 
            || '';
          
          if (customerRefNo) {
            setKycData(prev => ({
              ...prev,
              mobile: remitterData.mobile,
              customerreferenceno: customerRefNo
            }));
          }
        } else {
          setSuccess('Remitter not found. Please complete registration process.');
          setActiveTab('kyc'); // Move to KYC tab
        }
        
        if (response.data.data?.data) {
          const data = response.data.data.data;
          setRemitterData(prev => ({
            ...prev,
            name: data.name || '',
            email: data.email || '',
            address: data.address || '',
            pincode: data.pincode || '',
            city: data.city || '',
            state: data.state || '',
            pan_number: data.pan_number || '',
            aadhar_number: data.aadhar_number || ''
          }));
        }
      } else {
        setError(response.data.message || 'Failed to fetch remitter details');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch remitter details');
    } finally {
      setLoading(false);
    }
  };

  // Fetch Beneficiaries
  const fetchBeneficiaries = async () => {
    try {
      const response = await api.post('/api/dmt-bank4/beneficiary/fetch', {
        mobile: remitterData.mobile
      });

      if (response.data.success) {
        setBeneficiaries(response.data.data?.beneficiaries || []);
      }
    } catch (err) {
      console.error('Failed to fetch beneficiaries:', err);
    }
  };

  // Map backend KYC error codes to clear RD / fingerprint messages
  const getKycErrorMessage = (apiMessage) => {
    if (!apiMessage) return 'Remitter KYC failed. Please try again.';

    switch (apiMessage) {
      case 'PIDDATA_REQUIRED':
        return 'Fingerprint scan required – please connect RD device and capture again.';
      case 'INVALID_PIDDATA_FORMAT':
        return 'Invalid biometric data received – please rescan fingerprint from RD device.';
      default:
        return apiMessage;
    }
  };

  // Remitter KYC
  const handleRemitterKyc = async () => {
    if (!kycData.mobile || !kycData.kyc_data) {
      setError('Please fill all required fields');
      return;
    }

    setLoading(true);
    setError('');

    try {
      let payload = {
        mobile: kycData.mobile,
        kyc_type: kycData.kyc_type,
        kyc_data: kycData.kyc_data,
      };

      const trimmed = (kycData.kyc_data || '').trim();

      // If full <PidData> XML is present anywhere in the text → send it as piddata also
      // This handles cases like XML declarations or leading whitespace.
      if (trimmed.includes('<PidData')) {
        payload.piddata = trimmed;
      }

      const response = await api.post('/api/dmt-bank4/remitter/kyc', payload);

      if (response.data.success) {
        // Extract ekyc_id and stateresp from KYC response
        // PaySprint response can be nested in different ways
        const responseData = response.data.data?.data || response.data.data?.response || response.data.data || response.data || {};
        
        // Try multiple field name variations
        const ekycId = responseData.ekyc_id 
          || responseData.ekycid 
          || responseData.e_kyc_id 
          || responseData.eKycId
          || responseData.ekycId
          || responseData.EKYC_ID
          || responseData.EKYCID
          || '';
        
        const stateResp = responseData.stateresp 
          || responseData.state_resp 
          || responseData.state_response 
          || responseData.stateResp
          || responseData.stateResponse
          || responseData.STATERESP
          || responseData.STATE_RESP
          || '';
        
        // Log the response for debugging
        console.log('DMT Bank4 KYC Response:', {
          fullResponse: response.data,
          responseData: responseData,
          ekycId: ekycId,
          stateResp: stateResp,
          allKeys: Object.keys(responseData)
        });
        
        if (!ekycId || !stateResp) {
          setError(`E-KYC ID or State Response not found in response. Response keys: ${Object.keys(responseData).join(', ')}. Please check logs or contact support.`);
          setLoading(false);
          return;
        }

        // Store ekyc_id and stateresp in remitterData for Register Remitter
        setRemitterData(prev => ({
          ...prev,
          mobile: kycData.mobile,
          ekyc_id: ekycId,
          stateresp: stateResp,
          piddata: kycData.kyc_data || kycData.piddata || ''
        }));

        setSuccess('Remitter KYC completed successfully! Please proceed to Register Remitter and enter the OTP received via SMS.');
        setActiveTab('remitter'); // Move to Register Remitter tab
      } else {
        setError(getKycErrorMessage(response.data.message) || 'Remitter KYC failed');
      }
    } catch (err) {
      const apiMessage = err.response?.data?.message;
      setError(getKycErrorMessage(apiMessage));
    } finally {
      setLoading(false);
    }
  };

  // Register Remitter
  const handleRegisterRemitter = async () => {
    if (!remitterData.mobile || !remitterData.name) {
      setError('Please fill all required fields');
      return;
    }

    // Hard block if ekyc_id or stateresp is missing
    if (!remitterData.ekyc_id || !remitterData.stateresp) {
      setError('E-KYC ID and State Response are required. Please complete Remitter KYC first.');
      return;
    }

    if (!remitterData.otp) {
      setError('OTP is required. Please enter the OTP received via SMS after Remitter KYC.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Build payload with all required fields
      const payload = {
        mobile: remitterData.mobile,
        name: remitterData.name,
        email: remitterData.email || '',
        address: remitterData.address || '',
        pincode: remitterData.pincode || '',
        city: remitterData.city || '',
        state: remitterData.state || '',
        pan_number: remitterData.pan_number || '',
        aadhar_number: remitterData.aadhar_number || '',
        // Required fields from KYC
        ekyc_id: remitterData.ekyc_id,
        otp: remitterData.otp,
        stateresp: remitterData.stateresp,
        piddata: remitterData.piddata || '',
        accessmode: remitterData.accessmode || 'SITE | APP'
      };

      const response = await api.post('/api/dmt-bank4/remitter/register', payload);

      if (response.data.success) {
        setSuccess('Remitter registered successfully!');
        // Reset OTP after successful registration
        setRemitterData(prev => ({ ...prev, otp: '' }));
      } else {
        setError(response.data.message || 'Remitter registration failed');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Remitter registration failed');
    } finally {
      setLoading(false);
    }
  };

  // Register Beneficiary
  const handleRegisterBeneficiary = async () => {
    if (!beneficiaryData.mobile || !beneficiaryData.bene_name || !beneficiaryData.bene_account || !beneficiaryData.bene_ifsc || !beneficiaryData.bank_id) {
      setError('Please fill all required fields');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await api.post('/api/dmt-bank4/beneficiary/register', beneficiaryData);

      if (response.data.success) {
        setSuccess('Beneficiary registered successfully!');
        setBeneficiaryData({
          mobile: '',
          bene_name: '',
          bene_account: '',
          bene_ifsc: '',
          bank_id: '',
          bene_bank: '',
          bene_mobile: ''
        });
        // Refresh beneficiaries list
        handleFetchBeneficiaries();
      } else {
        setError(response.data.message || 'Beneficiary registration failed');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Beneficiary registration failed');
    } finally {
      setLoading(false);
    }
  };

  // Fetch Beneficiaries
  const handleFetchBeneficiaries = async () => {
    if (!beneficiaryData.mobile) {
      setError('Please enter mobile number');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await api.post('/api/dmt-bank4/beneficiary/fetch', {
        mobile: beneficiaryData.mobile
      });

      if (response.data.success) {
        setBeneficiaries(response.data.data?.data || []);
        setSuccess('Beneficiaries fetched successfully!');
      } else {
        setError(response.data.message || 'Failed to fetch beneficiaries');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch beneficiaries');
    } finally {
      setLoading(false);
    }
  };

  // Delete Beneficiary
  const handleDeleteBeneficiary = async (beneId) => {
    if (!window.confirm('Are you sure you want to delete this beneficiary?')) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await api.post('/api/dmt-bank4/beneficiary/delete', {
        mobile: beneficiaryData.mobile,
        bene_id: beneId
      });

      if (response.data.success) {
        setSuccess('Beneficiary deleted successfully!');
        // Refresh beneficiaries list
        handleFetchBeneficiaries();
      } else {
        setError(response.data.message || 'Failed to delete beneficiary');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete beneficiary');
    } finally {
      setLoading(false);
    }
  };

  // Send Transaction OTP
  const handleSendTransactionOTP = async () => {
    if (!transactionData.mobile || !transactionData.amount || !transactionData.bene_id) {
      setError('Please fill all required fields');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await api.post('/api/dmt-bank4/transaction/send-otp', {
        mobile: transactionData.mobile,
        amount: transactionData.amount,
        bene_id: transactionData.bene_id
      });

      if (response.data.success) {
        // Capture stateresp from Send OTP response for Process Transaction
        const stateresp = response.data.data?.stateresp || response.data.data?.data?.stateresp || '';
        if (stateresp) {
          setTransactionData(prev => ({ ...prev, stateresp }));
        }
        setSuccess('Transaction OTP sent successfully!');
      } else {
        setError(response.data.message || 'Failed to send transaction OTP');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to send transaction OTP');
    } finally {
      setLoading(false);
    }
  };

  // Process Transaction
  const handleProcessTransaction = async () => {
    if (!transactionData.mobile || !transactionData.amount || !transactionData.bene_id || !transactionData.otp) {
      setError('Please fill all required fields');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await api.post('/api/dmt-bank4/transaction/process', transactionData);

      if (response.data.success) {
        setSuccess('Transaction processed successfully!');
        setTransactionData({
          mobile: '',
          amount: '',
          bene_id: '',
          otp: '',
          reference_id: '',
          stateresp: ''
        });
        if (onSuccess) {
          onSuccess(response.data.data);
        }
      } else {
        setError(response.data.message || 'Transaction processing failed');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Transaction processing failed');
    } finally {
      setLoading(false);
    }
  };

  // Transaction Query
  const handleTransactionQuery = async () => {
    if (!statusData.reference_id) {
      setError('Please enter reference ID');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await api.post('/api/dmt-bank4/transaction/query', statusData);

      if (response.data.success) {
        setSuccess(`Transaction Status: ${response.data.data?.status || 'Unknown'}`);
      } else {
        setError(response.data.message || 'Failed to fetch transaction status');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch transaction status');
    } finally {
      setLoading(false);
    }
  };

  // Send Refund OTP
  const handleSendRefundOTP = async () => {
    if (!refundData.reference_id) {
      setError('Please enter reference ID');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await api.post('/api/dmt-bank4/refund/send-otp', {
        reference_id: refundData.reference_id
      });

      if (response.data.success) {
        setSuccess('Refund OTP sent successfully!');
      } else {
        setError(response.data.message || 'Failed to send refund OTP');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to send refund OTP');
    } finally {
      setLoading(false);
    }
  };

  // Claim Refund
  const handleClaimRefund = async () => {
    if (!refundData.reference_id || !refundData.otp) {
      setError('Please fill all required fields');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await api.post('/api/dmt-bank4/refund/claim', refundData);

      if (response.data.success) {
        setSuccess('Refund claimed successfully!');
        setRefundData({
          reference_id: '',
          otp: ''
        });
      } else {
        setError(response.data.message || 'Refund claim failed');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Refund claim failed');
    } finally {
      setLoading(false);
    }
  };

  // Reset form
  const handleReset = () => {
    setError('');
    setSuccess('');
    setRemitterData({
      mobile: '',
      name: '',
      email: '',
      address: '',
      pincode: '',
      city: '',
      state: '',
      pan_number: '',
      aadhar_number: ''
    });
    setKycData({
      mobile: '',
      kyc_type: 'aadhar',
      kyc_data: ''
    });
    setBeneficiaryData({
      mobile: '',
      bene_name: '',
      bene_account: '',
      bene_ifsc: '',
      bene_bank: '',
      bene_mobile: ''
    });
    setTransactionData({
      mobile: '',
      amount: '',
      bene_id: '',
      otp: '',
      reference_id: ''
    });
    setRefundData({
      reference_id: '',
      otp: ''
    });
    setStatusData({
      reference_id: ''
    });
    setBeneficiaries([]);
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <FaExchangeAlt className="me-2" />
          DMT Bank4 Service
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        {success && <Alert variant="success">{success}</Alert>}

        <Tabs
          activeKey={activeTab}
          onSelect={(k) => setActiveTab(k)}
          className="mb-3"
        >
          {/* Remitter Tab */}
          <Tab eventKey="remitter" title="Remitter">
            <Card>
              <Card.Header>
                <h5 className="mb-0">
                  <FaUser className="me-2" />
                  Remitter Management
                </h5>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Mobile Number *</Form.Label>
                      <Form.Control
                        type="text"
                        value={remitterData.mobile}
                        onChange={(e) => setRemitterData(prev => ({ ...prev, mobile: e.target.value }))}
                        placeholder="Enter 10-digit mobile number"
                        maxLength={10}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Button onClick={handleQueryRemitter} disabled={loading} className="w-100">
                      {loading ? <Spinner size="sm" className="me-2" /> : null}
                      Query Remitter
                    </Button>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Name *</Form.Label>
                      <Form.Control
                        type="text"
                        value={remitterData.name}
                        onChange={(e) => setRemitterData(prev => ({ ...prev, name: e.target.value }))}
                        placeholder="Enter full name"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Email</Form.Label>
                      <Form.Control
                        type="email"
                        value={remitterData.email}
                        onChange={(e) => setRemitterData(prev => ({ ...prev, email: e.target.value }))}
                        placeholder="Enter email address"
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-3">
                  <Form.Label>Address</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    value={remitterData.address}
                    onChange={(e) => setRemitterData(prev => ({ ...prev, address: e.target.value }))}
                    placeholder="Enter address"
                  />
                </Form.Group>

                <Row>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Pincode</Form.Label>
                      <Form.Control
                        type="text"
                        value={remitterData.pincode}
                        onChange={(e) => setRemitterData(prev => ({ ...prev, pincode: e.target.value }))}
                        placeholder="Enter pincode"
                        maxLength={6}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>City</Form.Label>
                      <Form.Control
                        type="text"
                        value={remitterData.city}
                        onChange={(e) => setRemitterData(prev => ({ ...prev, city: e.target.value }))}
                        placeholder="Enter city"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>State</Form.Label>
                      <Form.Control
                        type="text"
                        value={remitterData.state}
                        onChange={(e) => setRemitterData(prev => ({ ...prev, state: e.target.value }))}
                        placeholder="Enter state"
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>PAN Number</Form.Label>
                      <Form.Control
                        type="text"
                        value={remitterData.pan_number}
                        onChange={(e) => setRemitterData(prev => ({ ...prev, pan_number: e.target.value.toUpperCase() }))}
                        placeholder="Enter PAN number"
                        style={{ textTransform: 'uppercase' }}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Aadhar Number</Form.Label>
                      <Form.Control
                        type="text"
                        value={remitterData.aadhar_number}
                        onChange={(e) => setRemitterData(prev => ({ ...prev, aadhar_number: e.target.value }))}
                        placeholder="Enter Aadhar number"
                        maxLength={12}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                {/* OTP Field - Required after KYC */}
                {remitterData.ekyc_id && (
                  <Form.Group className="mb-3">
                    <Form.Label>OTP * <small className="text-muted">(Received via SMS after KYC)</small></Form.Label>
                    <Form.Control
                      type="text"
                      value={remitterData.otp}
                      onChange={(e) => setRemitterData(prev => ({ ...prev, otp: e.target.value.replace(/\D/g, '') }))}
                      placeholder="Enter 6-digit OTP"
                      maxLength={6}
                      required
                    />
                    <Form.Text className="text-muted">
                      Please enter the OTP received on your mobile number after completing Remitter KYC.
                    </Form.Text>
                  </Form.Group>
                )}

                {/* Info Alert if KYC not completed */}
                {!remitterData.ekyc_id && (
                  <Alert variant="warning" className="mb-3">
                    <strong>Note:</strong> Please complete Remitter KYC first before registering the remitter.
                  </Alert>
                )}

                <Button onClick={handleRegisterRemitter} disabled={loading} className="w-100">
                  {loading ? <Spinner size="sm" className="me-2" /> : null}
                  Register Remitter
                </Button>
              </Card.Body>
            </Card>
          </Tab>

          {/* KYC Tab */}
          <Tab eventKey="kyc" title="KYC">
            <Card>
              <Card.Header>
                <h5 className="mb-0">Remitter KYC</h5>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Mobile Number *</Form.Label>
                      <Form.Control
                        type="text"
                        value={kycData.mobile}
                        onChange={(e) => setKycData(prev => ({ ...prev, mobile: e.target.value }))}
                        placeholder="Enter 10-digit mobile number"
                        maxLength={10}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>KYC Type *</Form.Label>
                      <Form.Select
                        value={kycData.kyc_type}
                        onChange={(e) => setKycData(prev => ({ ...prev, kyc_type: e.target.value }))}
                      >
                        <option value="aadhar">Aadhar</option>
                        <option value="pan">PAN</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-3">
                  <Form.Label>KYC Data *</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={5}
                    value={kycData.kyc_data}
                    onChange={(e) => setKycData(prev => ({ ...prev, kyc_data: e.target.value }))}
                    placeholder="For Aadhaar KYC: Enter 12-digit Aadhaar number (e.g., 690179629474)&#10;For Biometric KYC: Paste full PidData XML from biometric device"
                  />
                  <Form.Text className="text-muted">
                    <strong>Note:</strong> If using biometric device (FM220U), paste the complete PidData XML here. 
                    For Aadhaar-only verification, enter just the Aadhaar number.
                  </Form.Text>
                </Form.Group>

                <Button onClick={handleRemitterKyc} disabled={loading} className="w-100">
                  {loading ? <Spinner size="sm" className="me-2" /> : null}
                  Process KYC
                </Button>
              </Card.Body>
            </Card>
          </Tab>

          {/* Beneficiary Tab */}
          <Tab eventKey="beneficiary" title="Beneficiary">
            <Card>
              <Card.Header>
                <h5 className="mb-0">
                  <FaUsers className="me-2" />
                  Beneficiary Management
                </h5>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Mobile Number *</Form.Label>
                      <Form.Control
                        type="text"
                        value={beneficiaryData.mobile}
                        onChange={(e) => setBeneficiaryData(prev => ({ ...prev, mobile: e.target.value }))}
                        placeholder="Enter 10-digit mobile number"
                        maxLength={10}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Button onClick={handleFetchBeneficiaries} disabled={loading} className="w-100">
                      {loading ? <Spinner size="sm" className="me-2" /> : null}
                      Fetch Beneficiaries
                    </Button>
                  </Col>
                </Row>

                <hr />

                <h6>Add New Beneficiary</h6>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Beneficiary Name *</Form.Label>
                      <Form.Control
                        type="text"
                        value={beneficiaryData.bene_name}
                        onChange={(e) => setBeneficiaryData(prev => ({ ...prev, bene_name: e.target.value }))}
                        placeholder="Enter beneficiary name"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Account Number *</Form.Label>
                      <Form.Control
                        type="text"
                        value={beneficiaryData.bene_account}
                        onChange={(e) => setBeneficiaryData(prev => ({ ...prev, bene_account: e.target.value }))}
                        placeholder="Enter account number"
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>IFSC Code *</Form.Label>
                      <Form.Control
                        type="text"
                        value={beneficiaryData.bene_ifsc}
                        onChange={(e) => setBeneficiaryData(prev => ({ ...prev, bene_ifsc: e.target.value.toUpperCase() }))}
                        placeholder="Enter IFSC code"
                        style={{ textTransform: 'uppercase' }}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>PaySprint Bank ID *</Form.Label>
                      <Form.Control
                        type="text"
                        value={beneficiaryData.bank_id}
                        onChange={(e) => setBeneficiaryData(prev => ({ ...prev, bank_id: e.target.value }))}
                        placeholder="Enter numeric bank ID"
                      />
                      <Form.Text muted>Use the exact bank ID provided in the PaySprint documentation.</Form.Text>
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-3">
                  <Form.Label>Bank Name</Form.Label>
                  <Form.Control
                    type="text"
                    value={beneficiaryData.bene_bank}
                    onChange={(e) => setBeneficiaryData(prev => ({ ...prev, bene_bank: e.target.value }))}
                    placeholder="Enter bank name (optional)"
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Beneficiary Mobile</Form.Label>
                  <Form.Control
                    type="text"
                    value={beneficiaryData.bene_mobile}
                    onChange={(e) => setBeneficiaryData(prev => ({ ...prev, bene_mobile: e.target.value }))}
                    placeholder="Enter beneficiary mobile number"
                    maxLength={10}
                  />
                </Form.Group>

                <Button onClick={handleRegisterBeneficiary} disabled={loading} className="w-100">
                  {loading ? <Spinner size="sm" className="me-2" /> : null}
                  Register Beneficiary
                </Button>

                {beneficiaries.length > 0 && (
                  <>
                    <hr />
                    <h6>Existing Beneficiaries</h6>
                    <ListGroup>
                      {beneficiaries.map((bene, index) => (
                        <ListGroup.Item key={index} className="d-flex justify-content-between align-items-center">
                          <div>
                            <strong>{bene.bene_name}</strong><br />
                            <small>{bene.bene_account} - {bene.bene_ifsc}</small>
                          </div>
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => handleDeleteBeneficiary(bene.bene_id)}
                          >
                            Delete
                          </Button>
                        </ListGroup.Item>
                      ))}
                    </ListGroup>
                  </>
                )}
              </Card.Body>
            </Card>
          </Tab>

          {/* Transaction Tab */}
          <Tab eventKey="transaction" title="Transaction">
            <Card>
              <Card.Header>
                <h5 className="mb-0">
                  <FaCreditCard className="me-2" />
                  Transaction Management
                </h5>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Mobile Number *</Form.Label>
                      <Form.Control
                        type="text"
                        value={transactionData.mobile}
                        onChange={(e) => setTransactionData(prev => ({ ...prev, mobile: e.target.value }))}
                        placeholder="Enter 10-digit mobile number"
                        maxLength={10}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Amount *</Form.Label>
                      <Form.Control
                        type="number"
                        value={transactionData.amount}
                        onChange={(e) => setTransactionData(prev => ({ ...prev, amount: e.target.value }))}
                        placeholder="Enter amount"
                        min="1"
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <div className="mb-3">
                  <Form.Label>Quick Amounts:</Form.Label>
                  <div className="d-flex flex-wrap gap-2">
                    {quickAmounts.map((amount) => (
                      <Button
                        key={amount}
                        variant="outline-primary"
                        size="sm"
                        onClick={() => setTransactionData(prev => ({ ...prev, amount: amount.toString() }))}
                      >
                        ₹{amount}
                      </Button>
                    ))}
                  </div>
                </div>

                <Form.Group className="mb-3">
                  <Form.Label>Beneficiary ID *</Form.Label>
                  {beneficiaries.length > 0 ? (
                    <Form.Select
                      value={transactionData.bene_id}
                      onChange={(e) => setTransactionData(prev => ({ ...prev, bene_id: e.target.value }))}
                    >
                      <option value="">Select Beneficiary</option>
                      {beneficiaries.map((bene, index) => (
                        <option key={index} value={bene.bene_id || bene.beneid}>
                          {bene.bene_name} - {bene.bene_id || bene.beneid} ({bene.bene_account})
                        </option>
                      ))}
                    </Form.Select>
                  ) : (
                    <>
                      <Form.Control
                        type="text"
                        value={transactionData.bene_id}
                        onChange={(e) => setTransactionData(prev => ({ ...prev, bene_id: e.target.value }))}
                        placeholder="Enter beneficiary ID (e.g., 7011630117 or 9999069988@ybl)"
                      />
                      <Form.Text className="text-muted">
                        UAT Format: Mobile number (7011630117) or Mobile+UPI (9999069988@ybl). 
                        Or fetch beneficiaries first to select from list.
                      </Form.Text>
                    </>
                  )}
                </Form.Group>

                <Button onClick={handleSendTransactionOTP} disabled={loading} className="w-100 mb-3">
                  {loading ? <Spinner size="sm" className="me-2" /> : null}
                  Send OTP
                </Button>

                <Form.Group className="mb-3">
                  <Form.Label>OTP *</Form.Label>
                  <Form.Control
                    type="text"
                    value={transactionData.otp}
                    onChange={(e) => setTransactionData(prev => ({ ...prev, otp: e.target.value }))}
                    placeholder="Enter 6-digit OTP"
                    maxLength={6}
                  />
                </Form.Group>

                <Button onClick={handleProcessTransaction} disabled={loading} className="w-100">
                  {loading ? <Spinner size="sm" className="me-2" /> : null}
                  Process Transaction
                </Button>
              </Card.Body>
            </Card>
          </Tab>

          {/* Status Tab */}
          <Tab eventKey="status" title="Status">
            <Card>
              <Card.Header>
                <h5 className="mb-0">Transaction Status</h5>
              </Card.Header>
              <Card.Body>
                <Form.Group className="mb-3">
                  <Form.Label>Reference ID *</Form.Label>
                  <Form.Control
                    type="text"
                    value={statusData.reference_id}
                    onChange={(e) => setStatusData(prev => ({ ...prev, reference_id: e.target.value }))}
                    placeholder="Enter reference ID"
                  />
                </Form.Group>

                <Button onClick={handleTransactionQuery} disabled={loading} className="w-100">
                  {loading ? <Spinner size="sm" className="me-2" /> : null}
                  Check Status
                </Button>
              </Card.Body>
            </Card>
          </Tab>

          {/* Refund Tab */}
          <Tab eventKey="refund" title="Refund">
            <Card>
              <Card.Header>
                <h5 className="mb-0">
                  <FaUndo className="me-2" />
                  Refund Management
                </h5>
              </Card.Header>
              <Card.Body>
                <Form.Group className="mb-3">
                  <Form.Label>Reference ID *</Form.Label>
                  <Form.Control
                    type="text"
                    value={refundData.reference_id}
                    onChange={(e) => setRefundData(prev => ({ ...prev, reference_id: e.target.value }))}
                    placeholder="Enter reference ID"
                  />
                </Form.Group>

                <Button onClick={handleSendRefundOTP} disabled={loading} className="w-100 mb-3">
                  {loading ? <Spinner size="sm" className="me-2" /> : null}
                  Send Refund OTP
                </Button>

                <Form.Group className="mb-3">
                  <Form.Label>OTP *</Form.Label>
                  <Form.Control
                    type="text"
                    value={refundData.otp}
                    onChange={(e) => setRefundData(prev => ({ ...prev, otp: e.target.value }))}
                    placeholder="Enter 6-digit OTP"
                    maxLength={6}
                  />
                </Form.Group>

                <Button onClick={handleClaimRefund} disabled={loading} className="w-100">
                  {loading ? <Spinner size="sm" className="me-2" /> : null}
                  Claim Refund
                </Button>
              </Card.Body>
            </Card>
          </Tab>
        </Tabs>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
        <Button variant="outline-warning" onClick={handleReset}>
          Reset
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default DMTBank4Modal;
